import { Edit, PlusSquare, Send, Trash2 } from "lucide-react";
import { useState, useEffect } from "react";
import {
  getTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  sendTemplate,
} from "../../../../src/api/communicationTemplates";
import NewTemplateModal from "../../../components/AdminPortal/NewTemplateModal";
import toast from "react-hot-toast";

export function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await getTemplates();
      setTemplates(data);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedTemplateIds.length === templates.length) {
      setSelectedTemplateIds([]);
    } else {
      setSelectedTemplateIds(templates.map((t) => t._id));
    }
  };

  const handleSelectOne = (id) => {
    setSelectedTemplateIds((prev) =>
      prev.includes(id)
        ? prev.filter((templateId) => templateId !== id)
        : [...prev, id]
    );
  };

  const handleCreate = async (template) => {
    setLoading(true);
    try {
      await createTemplate(template);
      fetchTemplates();
      toast.success("Template created successfully!");
    } finally {
      setLoading(false);
    }
  };

  const handleView = (template) => {
    setSelectedTemplate(template);
    setViewModalOpen(true);
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setEditModalOpen(true);
  };

 const handleSend = async (template) => {
  try {
    // Get JWT token from localStorage or your auth context
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("You must be logged in to send a template");
      return;
    }

    // Call the API to send the template
    const response = await sendTemplate(template._id, token);

    // Show success message from backend
    toast.success(response.message || "Template sent successfully!");

    // Optionally switch to bulk messaging tab with template pre-selected
    if (window.parent && window.parent.setActiveTab) {
      window.parent.setActiveTab("bulkMessaging", template);
    }
  } catch (error) {
    // Log full error for debugging
    console.error("Error sending template:", error.response?.data || error);

    // Show proper error toast
    const errorMessage =
      error.response?.data?.error || "Failed to send template. Please try again.";
    toast.error(errorMessage);
  }
};

  const handleDelete = (template) => {
    setSelectedTemplate(template);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteTemplate(selectedTemplate._id);
      setDeleteModalOpen(false);
      setSelectedTemplate(null);
      await fetchTemplates();
      toast.success("Template deleted successfully!");
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    }
  };

  const handleUpdate = async (templateData) => {
    setLoading(true);
    try {
      await updateTemplate(selectedTemplate._id, templateData);
      setEditModalOpen(false);
      setSelectedTemplate(null);
      await fetchTemplates();
      toast.success("Template updated successfully!");
    } catch (error) {
      console.error("Error updating template:", error);
      toast.error("Failed to update template");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-2xl text-gray-300">All Templates</h2>
        <button
          className="bg-yellow-400 text-black px-4 py-2 rounded-lg flex items-center w-full sm:w-auto justify-center"
          onClick={() => setModalOpen(true)}
        >
          <PlusSquare size={18} className="mr-1" /> Create New Template
        </button>
      </div>

      <div className="bg-[#131313] rounded-lg border border-[#333333] w-full overflow-x-auto scrollbar-hide">
        <table className="min-w-full text-sm border-separate border-spacing-0">
          <thead className="bg-[#131313] text-white">
            <tr>
              <th className="p-4 text-center border-x border-[#333333] rounded-tl-lg">
                <input
                  type="checkbox"
                  checked={
                    selectedTemplateIds.length === templates.length &&
                    templates.length > 0
                  }
                  onChange={handleSelectAll}
                  className="appearance-none h-5 w-5 border border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 relative cursor-pointer
                    before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                />
              </th>
              <th className="p-4 text-center border-x border-[#333333]">TEMPLATE</th>
              <th className="p-4 text-center border-x border-[#333333]">CATEGORY</th>
              <th className="p-4 text-center border-x border-[#333333]">MESSAGE PREVIEW</th>
              <th className="p-4 text-center border-x border-[#333333]">VARIABLES USED</th>
              <th className="p-4 text-center border-x border-[#333333]">LAST MODIFIED</th>
              <th className="p-4 text-center border-x border-[#333333] rounded-tr-lg">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="text-white bg-[#131313]">
            {loading ? (
              <tr>
                <td colSpan={7} className="p-6 text-center text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : templates.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-6 text-center text-gray-400">
                  No templates found.
                </td>
              </tr>
            ) : (
              templates.map((template, index) => {
                const isLast = index === templates.length - 1;
                return (
                  <tr
                    key={template._id}
                    className={`border-b border-[#333333] ${
                      isLast ? "last:rounded-b-lg" : ""
                    }`}
                  >
                    <td className="p-4 text-center border-x border-[#333333]">
                      <input
                        type="checkbox"
                        checked={selectedTemplateIds.includes(template._id)}
                        onChange={() => handleSelectOne(template._id)}
                        className="appearance-none h-5 w-5 border border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 relative cursor-pointer
                          before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                      />
                    </td>
                    <td className="p-4 text-center border-x border-[#333333]">{template.name}</td>
                    <td className="p-4 text-center border-x border-[#333333]">{template.type}</td>
                    <td className="p-4 text-center border-x border-[#333333]">
                      <button
                        className="text-[#FFCA20]"
                        onClick={() => handleView(template)}
                      >
                        View
                      </button>
                    </td>
                    <td className="p-4 text-center border-x border-[#333333]">{template.variables?.join(", ")}</td>
                    <td className="p-4 text-center border-x border-[#333333]">
                      {template.updatedAt
                        ? new Date(template.updatedAt).toLocaleDateString(
                            "en-GB",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            }
                          )
                        : "-"}
                    </td>
                    {/* Actions sections */}
                    <td className="p-4 text-center border-x border-[#333333]">
                      <div className="flex justify-center space-x-3">
                        <button
                          className="text-[#FFCA20] flex items-center gap-1 hover:text-yellow-300"
                          onClick={() => handleEdit(template)}
                        >
                          <Edit size={18} />
                          Edit
                        </button>
                        <button
                          className="text-green-400 flex items-center gap-1 hover:text-green-300"
                          onClick={() => handleSend(template)}
                        >
                          <Send size={18} />
                          Send
                        </button>
                        <button
                          className="text-red-400 flex items-center gap-1 hover:text-red-300"
                          onClick={() => handleDelete(template)}
                        >
                          <Trash2 size={18} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <NewTemplateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreate={handleCreate}
        loading={loading}
      />

      {/* Edit Template Modal */}
      <NewTemplateModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onCreate={handleUpdate}
        initialData={selectedTemplate}
        isEdit={true}
        loading={loading}
      />

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && selectedTemplate && (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50">
          <div className="bg-[#131313] text-white p-6 rounded-lg max-w-md w-full border border-gray-700 relative">
            <h2 className="text-xl font-bold mb-4 text-[#FFCA20]">
              Delete Template
            </h2>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete "{selectedTemplate.name}"? This
              action cannot be undone.
            </p>
            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                onClick={() => setDeleteModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {viewModalOpen && selectedTemplate && (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50">
          <div className="bg-[#131313] text-white p-6 rounded-lg max-w-md w-full border border-gray-700 relative overflow-y-auto max-h-[80vh]">
            <button
              className="absolute top-3 right-4 text-gray-400 hover:text-white text-lg"
              onClick={() => setViewModalOpen(false)}
            >
              ✕
            </button>
            <h2 className="text-xl font-bold mb-4 text-[#FFCA20]">
              Template Details
            </h2>
            <div className="mb-3">
              <p className="text-gray-400 text-sm">Template Name</p>
              <p className="text-lg">{selectedTemplate.name}</p>
            </div>
            <div className="mb-3">
              <p className="text-gray-400 text-sm">Type</p>
              <p className="text-lg">{selectedTemplate.type}</p>
            </div>
            <div className="mb-3">
              <p className="text-gray-400 text-sm">Message Body</p>
              <pre className="whitespace-pre-wrap bg-black p-3 rounded border border-gray-700 text-sm text-gray-200">
                {selectedTemplate.body}
              </pre>
            </div>
            <div className="mb-3">
              <p className="text-gray-400 text-sm">Variables</p>
              <p className="text-lg">
                {selectedTemplate.variables?.join(", ")}
              </p>
            </div>
            <div className="mb-3">
              <p className="text-gray-400 text-sm">Last Modified</p>
              <p className="text-lg">
                {selectedTemplate.updatedAt
                  ? new Date(selectedTemplate.updatedAt).toLocaleString("en-IN")
                  : "-"}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
